import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, signal, WritableSignal } from "@angular/core";
import { NavigationEnd, Router, RouterOutlet } from "@angular/router";
import { filter, map, Observable, Subscription } from "rxjs";
import { DashboardLayoutComponent } from "../../layout/dashboard-layout/dashboard-layout.component";
import { WavesBackgroundComponent } from "../../components/waves-background/waves-background.component";
import { AsyncPipe, CommonModule } from "@angular/common";
import { BlockingLoaderComponent, IModalService, modalServiceInjectionToken, ToastModule } from "@zaeper/angular-dashboard-view-lib";
import { ITwoFactorRestClient, MfaReminderBannerComponent, twoFactorRestClientInjectionToken } from "@zaeper/angular-2fa-lib";
import {
  BlockingLoadingIndicatorComponent
} from "../../components/loading-indicator/blocking-loading-indicator.component";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import {
  sessionStateServiceInjectionToken
} from "../../global-states/session/session-state-service-injection-token";
import { ISessionStateService } from "../../global-states/session/i-session-state.service";
import {
  DeveloperRegistrationModalComponent
} from "../../modals/developer-registration-modal/developer-registration-modal.component";


@Component( {
  selector: "app-app-outlet",
  templateUrl: "./app-outlet.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    DashboardLayoutComponent,
    WavesBackgroundComponent,
    CommonModule,
    AsyncPipe,
    ToastModule,
    ConfirmDialogModule,
    BlockingLoadingIndicatorComponent,
    BlockingLoaderComponent,
    MfaReminderBannerComponent
  ]
} )
export class AppOutletComponent implements OnInit, OnDestroy {
  public readonly routerPath$: Observable<string>;
  public readonly mfaEnabled: WritableSignal<boolean> = signal( true );

  private readonly _subscriptions: Subscription = new Subscription();
  /** The registration modal closes itself once the registration is active; it must not open twice. */
  private _registrationModalOpen: boolean = false;

  constructor(
    private readonly _router: Router,
    @Inject( modalServiceInjectionToken ) private readonly _modalService: IModalService,
    @Inject( sessionStateServiceInjectionToken ) private readonly _sessionStateService: ISessionStateService,
    @Inject( twoFactorRestClientInjectionToken ) private readonly _twoFactorRestClient: ITwoFactorRestClient
  ) {
    this.routerPath$ = this._router.events.pipe( filter( event => event instanceof NavigationEnd ) ).pipe( map( (event) => <NavigationEnd>event ) ).pipe( map( (event) => event.url ) );
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  ngOnInit(): void {
    this._subscriptions.add(
      this._sessionStateService.currentUser$.pipe(
        filter( user => user !== null && user.activatedAt === null && !this._registrationModalOpen )
      ).subscribe( () => {
        this._registrationModalOpen = true;
        this._modalService.open( DeveloperRegistrationModalComponent, {
          header: "Developer Registration",
          showHeader: false,
          closable: false,
          width: "540px",
          closeOnEscape: false,
          dismissableMask: false
        } );
      } )
    );

    this._twoFactorRestClient.getStatus().then( ( status ) => {
      this.mfaEnabled.set( status.enabled );
    } ).catch( () => {
      // keep banner hidden if status can't be loaded
    } );
  }

  public goToMfaSettings(): void {
    const language: string = this._router.url.split( "/" )[ 1 ] || "en";
    this._router.navigate( [ `/${ language }/mfa-settings` ] );
  }
}
